import React from 'react';
import Group from "../../dtos/Group";
import { useTranslation } from "react-i18next";
import { getDisplayableAge, restrictions as qidigoRestrictions } from "../../utils/qidigo";
import moment from 'moment';
import Warning from "./Warning";
import MembershipOffers from './MembershipOffers';
import * as _ from 'lodash'
import { useDateFormats } from '../../hooks/useDateFormats';
import {
    IMembership,
    IRestrictionByMembership,
    IRestrictionByMembershipTemplateAge,
    IRestrictionByMembershipTemplateGender,
} from '../../types';
import {formatDate} from "./FormattedDate";

function getMembershipRestrictionMessage({
    memberships,
    selectedMemberId,
    refreshOptions,
    shouldRenderMembershipOptions,
    getRestrictionDescription,
    toggleShoppingButtonDisability
}: {
    memberships: IMembership[]
    refreshOptions?: () => Promise<unknown>
    group?: Partial<Group>
    selectedMemberId?: string
    shouldRenderMembershipOptions?: boolean
    getRestrictionDescription: (membershipsList: string) => React.ReactNode
    toggleShoppingButtonDisability: (val: boolean) => void
}) {
    if (memberships.length === 0) {
        return null
    }

    if (shouldRenderMembershipOptions) {
        return (
            <div>
                {getRestrictionDescription('')}
                {
                    memberships.map(x =>
                        <MembershipOffers
                            id={x.id}
                            selectedMemberId={selectedMemberId}
                            refreshOptions={refreshOptions}
                            toggleShoppingButtonDisability={toggleShoppingButtonDisability}
                        />
                    )
                }
            </div>
        )
    }
    return getRestrictionDescription(memberships.map(x => x.name).join(', '))
}

function getMembershipTemplateAgeRestrictionMessage(
    restriction: IRestrictionByMembershipTemplateAge,
    translation: (str: string, options?: object) => string,
    dateFormat: string,
)
{
    const minAge = restriction.details.age_min;
    const maxAge = restriction.details.age_max;
    const ageUnit = restriction.details.age_display_type;
    const ageTextValue = {
        max_age: maxAge,
        min_age: minAge,
        max_age_unit: ageUnit === 'year' ? translation('restrictions.age.year', {count: maxAge ?? 0}) : translation('restrictions.age.month', {count: maxAge ?? 0}),
        min_age_unit: ageUnit === 'year' ? translation('restrictions.age.year', {count: minAge ?? 0}) : translation('restrictions.age.month', {count: minAge ?? 0})
    };
    if (restriction.code === qidigoRestrictions.membershipTemplateSubscriberAgeTooYoung ||
        restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerAgeTooYoung)
    {
        return translation(
            'restrictions.membershipTemplateFamilyOwnerTooYoung',
            {
                age_text: translation('restrictions.age.min_only', ageTextValue),
                validate_age_on: moment(restriction.details.validate_age_min_on).format(dateFormat)
            }
        );

    }

    if (restriction.code === qidigoRestrictions.membershipTemplateSubscriberAgeTooOld ||
        restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerAgeTooOld)
    {
        return translation(
            'restrictions.membershipTemplateFamilyOwnerTooOld',
            {
                age_text: translation('restrictions.age.max_only', ageTextValue),
                validate_age_on:  moment(restriction.details.validate_age_max_on).format(dateFormat)
            }
        );

    }

    if (
        restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerAgeNotSet ||
        restriction.code === qidigoRestrictions.membershipTemplateSubscriberAgeNotSet
    ) {
        return translation('restrictions.membershipTemplateFamilyOwnerNotSet');
    }
}

interface RestrictionMessageProps {
    restrictions: (string | IRestrictionByMembership | IRestrictionByMembershipTemplateAge)[],
    multipleGroups: boolean,
    refreshOptions?: () => Promise<unknown>
    group?: Partial<Group>
    selectedMemberId?: string
    shouldRenderMembershipOptions?: boolean
    renderContainer?: (messages: any[]) => React.ReactNode
    toggleShoppingButtonDisability: (val: boolean) => void
}

const RestrictionMessage: React.FC<RestrictionMessageProps> = ({
    restrictions,
    multipleGroups,
    group,
    refreshOptions,
    selectedMemberId,
    shouldRenderMembershipOptions = false,
    renderContainer = undefined,
    toggleShoppingButtonDisability,
}) => {
    const { t } = useTranslation();
    const { shortFormat } = useDateFormats()

    const membershipTemplateAgeRestrictionCodes = [
        qidigoRestrictions.membershipTemplateFamilyOwnerAgeNotSet,
        qidigoRestrictions.membershipTemplateFamilyOwnerAgeTooOld,
        qidigoRestrictions.membershipTemplateFamilyOwnerAgeTooYoung,
        qidigoRestrictions.membershipTemplateSubscriberAgeNotSet,
        qidigoRestrictions.membershipTemplateSubscriberAgeTooOld,
        qidigoRestrictions.membershipTemplateSubscriberAgeTooYoung,
    ];

    const membershipTemplateGenderRestrictionCodes = [
        qidigoRestrictions.membershipTemplateFamilyOwnerGenderNotRightGender,
        qidigoRestrictions.membershipTemplateSubscriberGenderNeedsFemale,
        qidigoRestrictions.membershipTemplateSubscriberGenderNeedsMale,
        qidigoRestrictions.membershipTemplateSubscriberGenderNotSet,
        qidigoRestrictions.membershipTemplateSubscriberGenderNotRightGender
    ];

    const membershipTemplateAgeRestrictions = restrictions
        .filter(x => typeof x !== 'string' && membershipTemplateAgeRestrictionCodes.includes(x.code)) as IRestrictionByMembershipTemplateAge[]

    const membershipTemplateGenderRestrictions = restrictions
        .filter(x => typeof x !== 'string' && membershipTemplateGenderRestrictionCodes.includes(x.code)) as IRestrictionByMembershipTemplateGender[]

    const restrictionWithMemberships = restrictions
        .filter(x =>
            typeof x !== 'string'
            && !membershipTemplateAgeRestrictionCodes.includes(x.code)
            && !membershipTemplateGenderRestrictionCodes.includes(x.code)
        ) as IRestrictionByMembership[]

    const groupedRestrictionsWithMemberships = _.toPairs(
        _.groupBy(restrictionWithMemberships, x => x.code)
    )
        .map(([code, restrictions]) => {
            return {
                code,
                memberships: _.uniq(_.flatten(restrictions.map(x => x.memberships)))
            }
        })

    const membershipsByPeriod = _.uniqBy(
        _.flatten(
            groupedRestrictionsWithMemberships
                .filter(x => x.code === qidigoRestrictions.periodRestrictedByMembership)
                .map(x => x.memberships)
        ),
        x => x.id
    )

    const membershipsByPlan = _.uniqBy(
        _.flatten(
            groupedRestrictionsWithMemberships
                .filter(x => x.code === qidigoRestrictions.planRestrictedByMembership)
                .map(x => x.memberships)
        ),
        x => x.id
    )

    const membershipsByPeriodAndPlan = _.intersectionBy(
        membershipsByPeriod,
        membershipsByPlan,
        x => x.id
    )

    const membershipsByPlanOnly = _.differenceBy(
        membershipsByPlan,
        membershipsByPeriodAndPlan,
        x => x.id
    )

    const membershipsByPeriodOnly = _.differenceBy(
        membershipsByPeriod,
        membershipsByPeriodAndPlan,
        x => x.id
    )

    const planNotYetActiveRestriction = restrictions.find((restriction:any) => restriction.code === qidigoRestrictions.planNotYetActive) as any;
    let planNotYetActiveMessage = '';
    if (planNotYetActiveRestriction) {
        planNotYetActiveMessage = t(
            'restrictions.planNotYetActive',
            {startDate: formatDate(moment(planNotYetActiveRestriction.details.start_date), {withTime: true})}
        )
    }

    let messages = [
            planNotYetActiveMessage,
        ...restrictions.filter(x => typeof x === 'string').map((restriction) => {
            let message;
            if (restriction === qidigoRestrictions.groupIsFull) {
                const oneGroupMessage = group?.waiting_list
                    ? t('restrictions.oneGroupIsFullWithWaitingList')
                    : t('restrictions.oneGroupIsFull')
            const onlyOneGroupMessage = group?.waiting_list
                    ? t('restrictions.groupIsFullWithWaitingList')
                    : t('restrictions.groupIsFull')
                message = multipleGroups ? oneGroupMessage : onlyOneGroupMessage;
            } else if (restriction === qidigoRestrictions.needsFemale) message = t('restrictions.needsFemale');
            else if (restriction === qidigoRestrictions.needsMale) message = t('restrictions.needsMale');
            else if (restriction === qidigoRestrictions.notRightGender) message = t('restrictions.notRightGender');
            else if (restriction === qidigoRestrictions.noSessionIsAvailable) message = t('restrictions.noSessionIsAvailable');
            else if (restriction === qidigoRestrictions.tooYoung) {
                message = multipleGroups
                    ? t('restrictions.tooYoungForOneGroup')
                    : t('restrictions.tooYoung', { minimumAge: getDisplayableAge(t, group?.restriction_age_min || 0), validateDate: moment(group?.validate_age_min_on).format(shortFormat) });
            }
            else if (restriction === qidigoRestrictions.tooOld) {
                message = multipleGroups
                    ? t('restrictions.tooOldForOneGroup')
                    : t('restrictions.tooOld', { maximumAge: getDisplayableAge(t, group?.restriction_age_max || 0), validateDate: moment(group?.validate_age_max_on).format(shortFormat) });
            }
            else if (restriction === qidigoRestrictions.maxRegistrations) message = t('restrictions.maxRegistrations');
            else if (restriction === qidigoRestrictions.periodRestrictsCity) message = t('restrictions.periodRestrictsCity');
            else if (restriction === qidigoRestrictions.periodRestrictedByGroup) message = t('restrictions.periodRestrictedByGroup');
            else if (restriction === qidigoRestrictions.planNotActive) message = t('restrictions.planNotActive');
            else if (restriction === qidigoRestrictions.organizationNeedsPhoneNumber) message = t('restrictions.organizationNeedsPhoneNumber');
            else if (restriction === qidigoRestrictions.organizationNeedsAddress) message = t('restrictions.organizationNeedsAddress');
            return message;
        }),
        ...membershipTemplateAgeRestrictions.map((restriction: IRestrictionByMembershipTemplateAge) => getMembershipTemplateAgeRestrictionMessage(restriction, t, shortFormat)),
        ...membershipTemplateGenderRestrictions.map((restriction: IRestrictionByMembershipTemplateGender) => {
            if (restriction.code === qidigoRestrictions.membershipTemplateSubscriberGenderNeedsFemale) {
                return t('restrictions.membershipTemplateSubscriberGenderNeedsFemale');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateSubscriberGenderNeedsMale) {
                return t('restrictions.membershipTemplateSubscriberGenderNeedsMale');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateSubscriberGenderNotSet) {
                return t('restrictions.membershipTemplateSubscriberGenderNotSet');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateSubscriberGenderNotRightGender) {
                return t('restrictions.membershipTemplateSubscriberGenderNotRightGender');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerGenderNeedsFemale) {
                return t('restrictions.membershipTemplateFamilyOwnerGenderNeedsFemale');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerGenderNeedsMale) {
                return t('restrictions.membershipTemplateFamilyOwnerGenderNeedsMale');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerGenderNotSet) {
                return t('restrictions.membershipTemplateFamilyOwnerGenderNotSet');
            } else if (restriction.code === qidigoRestrictions.membershipTemplateFamilyOwnerGenderNotRightGender) {
                return t('restrictions.membershipTemplateFamilyOwnerGenderNotRightGender');
            } else {
                return t('restrictions.default');
            }
        }),
        getMembershipRestrictionMessage({
            memberships: membershipsByPeriodAndPlan,
            shouldRenderMembershipOptions,
            selectedMemberId,
            refreshOptions,
            getRestrictionDescription: membershipsList => (
                <div>
                    <li>
                        {t('restrictions.periodRestrictedByMembership', { membershipsList })}
                    </li>
                    <li>
                        {t('restrictions.planRestrictedByMembership', { membershipsList })}
                    </li>
                </div>
            ),
            toggleShoppingButtonDisability,
        }),
        getMembershipRestrictionMessage({
            memberships: membershipsByPlanOnly,
            shouldRenderMembershipOptions,
            selectedMemberId,
            refreshOptions,
            getRestrictionDescription: membershipsList => t('restrictions.planRestrictedByMembership', { membershipsList }),
            toggleShoppingButtonDisability,
        }),
        getMembershipRestrictionMessage({
            memberships: membershipsByPeriodOnly,
            shouldRenderMembershipOptions,
            selectedMemberId,
            refreshOptions,
            getRestrictionDescription: membershipsList => t('restrictions.periodRestrictedByMembership', { membershipsList }),
            toggleShoppingButtonDisability,
        }),
    ]
        .filter(x => Boolean(x))
    return (
        <Warning messages={messages} />
    )
};

export default RestrictionMessage;
